import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../design-system';
import styles from './WizardShell.module.css';

/**
 * The chrome the three wizards share.
 *
 * It owns only the frame — overlay, header, step rail, footer — and takes the
 * step content as children. Each flow keeps its own state and validation,
 * because "can I advance" means something different in each: dates that clear
 * the concurrency cap, a certificate folio, an hour window.
 *
 * Three behaviours the design implies but does not draw, all of which matter
 * for a form this long: Escape closes it, focus moves inside on open, and the
 * page behind it does not scroll — without the last one, dragging inside the
 * date picker on a phone scrolls the list underneath.
 */

export interface WizardStep {
  readonly label: string;
}

export interface WizardShellProps {
  readonly eyebrow: string;
  readonly title: string;
  readonly steps: readonly WizardStep[];
  /** Zero-based. */
  readonly current: number;
  readonly nextLabel: string;
  readonly nextDisabled?: boolean;
  readonly busy?: boolean;
  readonly onBack: () => void;
  readonly onNext: () => void;
  readonly onClose: () => void;
  readonly children: ReactNode;
  /** The context panel beside the steps. */
  readonly aside?: ReactNode;
  /** Rendered above the step rail — draft notices, reschedule banners. */
  readonly banner?: ReactNode;
}

export const WizardShell = ({
  eyebrow,
  title,
  steps,
  current,
  nextLabel,
  nextDisabled = false,
  busy = false,
  onBack,
  onNext,
  onClose,
  children,
  aside,
  banner,
}: WizardShellProps): ReactNode => {
  const panelRef = useRef<HTMLDivElement>(null);

  /*
   * The latest `onClose`, without it being an effect dependency.
   *
   * Callers pass an inline arrow, so the prop is a new function on every render
   * of the screen holding the wizard. With `onClose` in the dependency array the
   * whole effect re-ran on each of those renders — and it calls `focus()`, so it
   * yanked focus back to the panel mid-keystroke: type a letter in any field,
   * lose the caret, type the next letter somewhere else. The search box in the
   * replacement step is where it became impossible to ignore.
   */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  /*
   * Mount only. Focus moves into the dialog once, when it opens — moving it
   * again later is the bug above, and stealing focus from somebody mid-sentence
   * is worse than the accessibility nicety it was meant to be.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    panelRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  /*
   * Por un portal, como `Modal`, y por la misma razón exacta.
   *
   * El overlay es `position: fixed`, y un `fixed` deja de anclarse a la ventana en
   * cuanto **cualquier** ancestro tiene `transform`, `filter`, `perspective`,
   * `will-change` o `contain`: ese ancestro pasa a ser su bloque contenedor. El
   * asistente de traspaso se montó dentro de la tarjeta de identidad de una ficha,
   * que lleva `transform`, y el modal apareció incrustado en la página en vez de
   * sobre ella — medido: 966px de ancho empezando en y=159, dentro de una ventana
   * de 1280.
   *
   * `Modal` ya llevaba portal y su comentario avisaba de que el arreglo no puede
   * ser «no montes diálogos dentro de una tarjeta», porque la próxima trampa será
   * otra propiedad en otro ancestro. Los tres asistentes se salvaban por dónde
   * estaban montados, no por diseño.
   */
  return createPortal(
    <div
      className={styles.overlay}
      onClick={(event) => {
        // Only a click on the backdrop itself closes; one that started inside
        // the card and ended on the overlay must not throw the form away.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={styles.card} ref={panelRef} role="dialog" aria-modal="true" tabIndex={-1}>
        <div className={styles.head}>
          <div>
            <div className={styles.eyebrow}>{eyebrow}</div>
            <h1 className={styles.title}>{title}</h1>
          </div>
          <button type="button" className={styles.close} aria-label="Cerrar" onClick={onClose}>
            ×
          </button>
        </div>

        {banner}

        <div className={styles.steps}>
          {steps.map((step, index) => (
            <div
              key={step.label}
              className={cx(
                styles.step,
                index < current && styles.stepDone,
                index === current && styles.stepCurrent,
              )}
              aria-current={index === current ? 'step' : undefined}
            >
              <span className={styles.stepNumber}>
                {index < current ? '✓' : index + 1}
              </span>
              {step.label}
            </div>
          ))}
        </div>

        <div className={styles.body}>
          <div className={styles.panel}>
            {children}

            <div className={styles.footer}>
              <button
                type="button"
                className={styles.back}
                disabled={current === 0 || busy}
                onClick={onBack}
              >
                Atrás
              </button>
              <button
                type="button"
                className={styles.next}
                disabled={nextDisabled || busy}
                onClick={onNext}
              >
                {busy ? 'Enviando…' : nextLabel}
              </button>
            </div>
          </div>

          {aside}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export { styles as wizardStyles };
